import axios, { ApiRoutes, AppError, ErrorResponse } from "@/api/client";
import { createResource, deleteResource, getAllPages, getResource, getResourcePage } from "@/api/helpers";
import { ExistingProduct, PaginationParams, PaginationResponse, Product } from "@/types";

// Fetch paginated products
const getProducts = async (paginationParams: PaginationParams): Promise<PaginationResponse<ExistingProduct>> => {
  return getResourcePage<ExistingProduct, { products: ExistingProduct[] | null }>(
    ApiRoutes.EXPORT_PRODUCTS,
    paginationParams,
    (response) => response.data.data.products ?? [],
    "Failed to fetch products.",
  );
};

// Fetch all products (across all pages)
const getAllProducts = async (): Promise<ExistingProduct[]> => getAllPages(getProducts);

// Fetch a single product by ID
const getProduct = async (productID: ExistingProduct["id"]): Promise<ExistingProduct> =>
  getResource<ExistingProduct, { product: ExistingProduct }>(
    `${ApiRoutes.EXPORT_PRODUCTS}/${productID}`,
    (response) => response.data.data.product,
    "Failed to fetch product.",
  );

const postProduct = (product: Product): Promise<ExistingProduct> =>
  createResource<Product, ExistingProduct>(
    ApiRoutes.EXPORT_PRODUCTS,
    product,
    "product",
    "product",
    "Failed to post product.",
  );

const patchProduct = (id: string, fields: Partial<Product>): Promise<void> =>
  axios
    .patch(`${ApiRoutes.EXPORT_PRODUCTS}/${id}`, {
      product: fields,
    })
    .then(() => undefined)
    .catch((reason: ErrorResponse) => {
      const e: AppError = {
        error: {
          message: reason?.response?.data?.message || "Failed to update product.",
          suppressGlobalNotification: false,
          statusCode: reason.response?.status,
        },
      };
      throw e;
    });

const deleteProduct = (id: string): Promise<void> =>
  deleteResource(`${ApiRoutes.EXPORT_PRODUCTS}/${id}`, "Failed to delete product.");

const ProductsService = {
  getProducts,
  getAllProducts,
  getProduct,
  postProduct,
  patchProduct,
  deleteProduct,
};
export default ProductsService;
